import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { RunnerJob } from '../../models/test-run.model';
import {
  CancelFailureKey,
  ExecutionJob,
  ExecutionJobService,
  ExecutionJobStatus,
  cancelFailureKey,
  isFinalStatus,
} from '../../services/execution-job.service';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { formatJobDuration } from '../../shared/duration.util';
import { ResPillComponent, ResPillStatus } from '../res-pill/res-pill.component';

const DURATION_TICK_MS = 1000;

const PILL_STATUS: Record<ExecutionJobStatus, ResPillStatus> = {
  QUEUED: 'not-run',
  RUNNING: 'running',
  PASSED: 'passed',
  FAILED: 'failed',
  ERROR: 'failed',
  CANCELLED: 'skipped',
};

interface SublinePart {
  kind: 'engine' | 'os' | 'duration';
  value: string;
}

type ProtocolState = 'closed' | 'loading' | 'loaded' | 'error';

/** Was die Karte selbst ueber den Auftrag weiss, frischer als der Runner-Zustand. */
interface KnownJob {
  jobId: string;
  status: ExecutionJobStatus;
  cancelRequested: boolean;
}

/**
 * Der Rechner hinter einem Durchlauf und der Stand seines Auftrags (NEX-1659), im Stil der
 * Pipeline-Karte der Live-Ansicht. Das Protokoll kann bis zu 20.000 Zeilen haben und steckt
 * darum nicht im Runner-Zustand: Die Karte holt es erst beim Aufklappen, und jedes Mal neu.
 */
@Component({
  selector: 'app-runner-job-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslatePipe,
    ResPillComponent,
    ConfirmDialogComponent,
  ],
  templateUrl: './runner-job-card.component.html',
  styleUrl: './runner-job-card.component.scss',
})
export class RunnerJobCardComponent {
  private readonly jobService = inject(ExecutionJobService);

  readonly projectId = input.required<string>();
  readonly runnerJob = input.required<RunnerJob>();
  /** Ohne Schrittergebnisse sagt die Fehlerflaeche zusaetzlich, dass die Schritte nicht liefen. */
  readonly hasStepResults = input(true);
  /** callerCanWrite des Runner-Zustands; ohne Schreibrecht kein Abbruch (NEX-1663). */
  readonly canWrite = input(false);
  /** Der Auftrag hat sich geaendert; die Seite laedt den Runner-Zustand neu. */
  readonly changed = output<void>();

  /**
   * Der Runner-Zustand kennt cancelRequested nicht, und ein Abbruch schickt kein Live-Ereignis.
   * Bis der Runner-Zustand einen Endstand meldet, gilt darum, was die Karte selbst erfahren hat.
   */
  private readonly known = signal<KnownJob | null>(null);
  protected readonly status = computed<ExecutionJobStatus>(() => {
    const job = this.runnerJob();
    const known = this.known();
    return known?.jobId === job.jobId && !isFinalStatus(job.status) ? known.status : job.status;
  });
  protected readonly cancelRequested = computed(() => {
    const known = this.known();
    return known?.jobId === this.runnerJob().jobId && known.cancelRequested && !isFinalStatus(this.status());
  });

  protected readonly pillStatus = computed(() => PILL_STATUS[this.status()]);
  protected readonly running = computed(() => this.status() === 'RUNNING');
  protected readonly canCancel = computed(() => this.canWrite() && !isFinalStatus(this.status()));
  protected readonly cancelConfirming = signal(false);
  protected readonly cancelling = signal(false);
  protected readonly cancelError = signal<CancelFailureKey | null>(null);
  private lookedUpJobId: string | null = null;
  private lookupRequest: Subscription | null = null;
  private cancelRequest: Subscription | null = null;
  private tracking: Subscription | null = null;
  protected readonly titleId = computed(() => `runner-job-title-${this.runnerJob().jobId}`);
  protected readonly protocolId = computed(() => `runner-job-protocol-${this.runnerJob().jobId}`);

  private readonly now = signal(Date.now());
  private readonly durationTicking = computed(() => {
    const job = this.runnerJob();
    return this.running() && !!job.startedAt && !job.finishedAt;
  });

  protected readonly duration = computed(() => {
    const job = this.runnerJob();
    if (!job.startedAt) {
      return null;
    }
    const end = job.finishedAt ? Date.parse(job.finishedAt) : this.durationTicking() ? this.now() : NaN;
    const elapsed = end - Date.parse(job.startedAt);
    return Number.isNaN(elapsed) ? null : formatJobDuration(elapsed);
  });

  protected readonly subline = computed<SublinePart[]>(() => {
    const job = this.runnerJob();
    const duration = this.duration();
    const parts: SublinePart[] = [];
    if (job.engine) {
      parts.push({ kind: 'engine', value: job.engine });
    }
    if (job.os) {
      parts.push({ kind: 'os', value: job.os });
    }
    if (duration) {
      parts.push({ kind: 'duration', value: duration });
    }
    return parts;
  });

  /** Nur bei einem Fehler-Auftrag: Dann erklaert der stumme Rechner, warum der Auftrag scheiterte. */
  protected readonly unreachableSince = computed(() => {
    const job = this.runnerJob();
    return this.status() === 'ERROR' && job.runnerOnline === false ? (job.runnerLastSeenAt ?? null) : null;
  });

  protected readonly protocolState = signal<ProtocolState>('closed');
  private readonly protocol = signal<string[]>([]);
  protected readonly protocolText = computed(() => this.protocol().join('\n'));
  protected readonly expanded = computed(() => this.protocolState() !== 'closed');
  private protocolRequest: Subscription | null = null;

  constructor() {
    effect((onCleanup) => {
      if (!this.durationTicking()) {
        return;
      }
      this.now.set(Date.now());
      const handle = setInterval(() => this.now.set(Date.now()), DURATION_TICK_MS);
      onCleanup(() => clearInterval(handle));
    });
    effect(() => {
      const job = this.runnerJob();
      if (this.canWrite() && job.status === 'RUNNING' && this.lookedUpJobId !== job.jobId) {
        this.lookedUpJobId = job.jobId;
        this.lookUpCancelRequested(job.jobId);
      }
    });
    inject(DestroyRef).onDestroy(() => {
      this.stopLoading();
      this.lookupRequest?.unsubscribe();
      this.cancelRequest?.unsubscribe();
      this.tracking?.unsubscribe();
    });
  }

  protected askCancel(): void {
    this.cancelError.set(null);
    this.cancelConfirming.set(true);
  }

  protected dismissCancel(): void {
    this.cancelConfirming.set(false);
  }

  protected confirmCancel(): void {
    if (this.cancelling()) {
      return;
    }
    this.cancelConfirming.set(false);
    this.cancelling.set(true);
    this.cancelRequest = this.jobService.cancel(this.projectId(), this.runnerJob().jobId).subscribe({
      next: (job) => {
        this.cancelling.set(false);
        this.remember(job);
        this.changed.emit();
      },
      error: (error: unknown) => {
        this.cancelling.set(false);
        const key = cancelFailureKey(error);
        this.cancelError.set(key);
        if (key === 'alreadyFinished') {
          this.changed.emit();
        }
      },
    });
  }

  /** Nach einem Neuladen der Seite waere der Knopf sonst wieder frei, obwohl der Abbruch laeuft. */
  private lookUpCancelRequested(jobId: string): void {
    this.lookupRequest?.unsubscribe();
    this.lookupRequest = this.jobService.get(this.projectId(), jobId).subscribe({
      next: (job) => {
        if (job.cancelRequested && !this.cancelRequested()) {
          this.remember(job);
        }
      },
      error: () => undefined,
    });
  }

  private remember(job: ExecutionJob): void {
    const previous = this.known();
    const cancelRequested = job.cancelRequested || (previous?.jobId === job.id && previous.cancelRequested);
    this.known.set({ jobId: job.id, status: job.status, cancelRequested });
    if (cancelRequested && !isFinalStatus(job.status) && !this.tracking) {
      this.track(job.id);
    }
  }

  /** Der Endstand eines Abbruchs kommt nicht ueber den Live-Feed; die Karte fragt selbst nach. */
  private track(jobId: string): void {
    this.tracking = this.jobService.poll(this.projectId(), jobId).subscribe({
      next: (job) => {
        if (isFinalStatus(job.status)) {
          this.tracking = null;
        }
        this.remember(job);
        if (isFinalStatus(job.status)) {
          this.changed.emit();
        }
      },
      error: () => {
        this.tracking = null;
        this.changed.emit();
      },
    });
  }

  protected toggleProtocol(): void {
    if (this.expanded()) {
      this.stopLoading();
      this.protocolState.set('closed');
      return;
    }
    this.loadProtocol();
  }

  protected loadProtocol(): void {
    this.stopLoading();
    this.protocolState.set('loading');
    this.protocolRequest = this.jobService.get(this.projectId(), this.runnerJob().jobId).subscribe({
      next: (job) => {
        this.protocol.set(job.protocol);
        this.protocolState.set('loaded');
      },
      error: () => this.protocolState.set('error'),
    });
  }

  private stopLoading(): void {
    this.protocolRequest?.unsubscribe();
    this.protocolRequest = null;
  }
}
